import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import {
  sendCodeToFactor,
  signIn,
  verifyFactor,
  resendCodeToFactor,
  exchangeToken,
  activateFactor,
  clearActivateFactor,
  enrollPhoneFactor,
  skipEnrollFactor,
  onNewRegistration,
  cancelTransaction,
  prevTransaction,
  goBack,
  enrollEmailFactor,
  impersonate,
  federate,
  stepup,
  updateLoginClient,
  logout,
  impersonatorLogout,
  loginTimeout,
  acceptNewUserAgreement,
  updateUserAgreementVersion,
  scheduleLoadingSpinnerWithDelay,
  disableLoadingSpinner,
  addTokenText,
  addStructIdLevel3,
  resetPassword,
  preLoadProfile,
  clearSignInError,
  signInFailed,
  staySignIn,
  setLockoutLoginRedirectUrl,
} from './login.actions';
import {
  FactorType,
  FactorVerify,
  AuthTransaction,
  SignInOptions,
  LoginClient,
  ResetPasswordOptions,
} from './login.models';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  getFactorByType,
  isTransactionInProgress,
  signInError,
  transaction,
  canGoBack as canGoBackSelector,
  hasTransaction,
  hasBeenSignedIn,
  newPhoneNumber,
  registrationPhoneNumber,
  isANewUser,
  shouldSkipMfaWelcomeScreen,
  hasValidCredentials,
  isAfterRegistration,
  getEapToken,
  getLoginClientId,
  stepupToken,
  userAgreementVersion,
  isSpinnerShown,
  spinnerCount,
  getTokenText,
  getStructIdLevel3,
  resetPasswordError,
  isProfileLoading,
  getLockOutLoginRedirectUrl,
} from './login.selectors';

@Injectable({ providedIn: 'root' })
export class LoginFacade {
  postloginPopupRedirectUrl: string;

  mfaTimeoutDialog$ = new Subject<boolean>();
  transaction$: Observable<AuthTransaction> = this._store$
    .select(transaction)
    .pipe(filter((v): v is AuthTransaction => !!v));

  hasTransaction$ = this._store$.select(hasTransaction);

  signInError$ = this._store$.select(signInError);
  isTransactionInProgress$ = this._store$.select(isTransactionInProgress);
  newPhoneNumber$ = this._store$.select(newPhoneNumber);
  registrationPhoneNumber$ = this._store$.select(registrationPhoneNumber);
  canGoBack$ = this._store$.select(canGoBackSelector);
  hasBeenSignedIn$ = this._store$.select(hasBeenSignedIn);
  isANewUser$ = this._store$.select(isANewUser);
  shouldSkipMfaWelcomeScreen$ = this._store$.select(shouldSkipMfaWelcomeScreen);
  hasValidCredentials$ = this._store$.select(hasValidCredentials);
  isAfterRegistration$ = this._store$.select(isAfterRegistration);
  eapToken$ = this._store$.select(getEapToken);
  clientId$ = this._store$.select(getLoginClientId);
  stepupToken$ = this._store$.select(stepupToken);
  userAgreementVersion$ = this._store$.select(userAgreementVersion);
  isLoadingSpinnerEnabled$ = this._store$.select(isSpinnerShown);
  isProfileLoading$ = this._store$.select(isProfileLoading);
  loadingSpinnerCount$ = this._store$.select(spinnerCount);
  tokenText$ = this._store$.select(getTokenText);
  structIdLevel3$ = this._store$.select(getStructIdLevel3);
  resetPasswordError$ = this._store$.select(resetPasswordError);
  lockOutLoginRedirectUrl$ = this._store$.select(getLockOutLoginRedirectUrl);

  getFactorByType(factorType: FactorType) {
    return this._store$.select(getFactorByType, { factorType });
  }

  constructor(private _store$: Store) {}

  signIn(options: SignInOptions) {
    this._store$.dispatch(signIn({ options }));
  }

  staySignIn() {
    this._store$.dispatch(staySignIn());
  }

  setPostloginPopupRedirectUrl(url: string) {
    this.postloginPopupRedirectUrl = url;
  }

  readPostloginPopupRedirectUrl() {
    const url = this.postloginPopupRedirectUrl;
    this.postloginPopupRedirectUrl = '';
    return url;
  }

  setLockoutLoginRedirectUrl(url: string) {
    this._store$.dispatch(setLockoutLoginRedirectUrl({ url }));
  }

  sendCodeToFactor(factor: FactorVerify, canGoBack: boolean) {
    this._store$.dispatch(sendCodeToFactor({ factor, canGoBack }));
  }

  resendCodeToFactor() {
    this._store$.dispatch(resendCodeToFactor());
  }

  verifyFactor({
    code,
    rememberDevice,
  }: {
    code: string;
    rememberDevice: boolean;
  }) {
    this._store$.dispatch(verifyFactor({ code, rememberDevice }));
  }

  exchangeToken(sessionToken: string) {
    this._store$.dispatch(exchangeToken({ sessionToken }));
  }

  activateFactor(code: string) {
    this._store$.dispatch(activateFactor({ code }));
  }
  passwordExpiryAction(reason: number) {
    this._store$.dispatch(signInFailed({ reason }));
  }
  clearActivatedFactor() {
    this._store$.dispatch(clearActivateFactor());
  }
  enrollEmailFactor() {
    return this._store$.dispatch(enrollEmailFactor());
  }

  enrollPhoneFactor(phoneNumber: string) {
    this._store$.dispatch(enrollPhoneFactor({ phoneNumber }));
  }

  skipEnrollFactor() {
    this._store$.dispatch(skipEnrollFactor());
  }

  cancelTransaction() {
    this._store$.dispatch(cancelTransaction());
  }

  prevTransaction() {
    this._store$.dispatch(prevTransaction());
  }

  goBack() {
    this._store$.dispatch(goBack());
  }

  impersonate() {
    this._store$.dispatch(impersonate());
  }

  federate() {
    this._store$.dispatch(federate());
  }

  stepup() {
    this._store$.dispatch(stepup());
  }

  updateLoginClient(clientId: LoginClient) {
    this._store$.dispatch(updateLoginClient({ clientId }));
  }

  logout(userAction: 'logout' | 'inactive') {
    this._store$.dispatch(logout({ userAction }));
  }

  impersonatorLogout() {
    this._store$.dispatch(impersonatorLogout());
  }

  loginTimeout() {
    this._store$.dispatch(loginTimeout());
  }

  updateUserAgreementVersion(version: string) {
    this._store$.dispatch(updateUserAgreementVersion({ version }));
  }

  acceptNewUserAgreement() {
    this._store$.dispatch(acceptNewUserAgreement());
  }

  scheduleLoadingSpinnerWithDelay() {
    this._store$.dispatch(scheduleLoadingSpinnerWithDelay());
  }

  disableLoadingSpinner() {
    this._store$.dispatch(disableLoadingSpinner());
  }

  addTokenText(tokenText: string) {
    this._store$.dispatch(addTokenText({ tokenText }));
  }

  addStructIdLevel3(structIdLevel3: string) {
    this._store$.dispatch(addStructIdLevel3({ structIdLevel3 }));
  }

  resetPassword(options: ResetPasswordOptions) {
    this._store$.dispatch(resetPassword({ options }));
  }

  preLoadProfile(ssoId: string) {
    this._store$.dispatch(preLoadProfile({ ssoId }));
  }

  clearSignInError() {
    this._store$.dispatch(clearSignInError());
  }
}
